/* eslint-disable no-param-reassign */
import { useState, useCallback } from 'react';
import { showErrorMessage } from '../../../../../../utils';
import { V2Type } from '../credentialV2Utils';
import { useCredentialNumber } from './useCredentialNumber';
import { useCredentialArtifact } from './useCredentialArtifact';
import { useCredentialNpi } from './useCredentialNpi';
import { useCredentialReference } from './useCredentialReference';

const getCredKey = (cred) => cred.id || cred.credential;

const findOriginalCred = (authorization, cred) => {
  const origCreds = authorization?.credentials || [];
  if (cred.id) return origCreds.find((c) => c?.id === cred.id);
  return origCreds.find((c) => c?.credential === cred.credential);
};

const hasCredChanged = (current, original) => {
  if (!original) return true;
  return JSON.stringify(current) !== JSON.stringify(original);
};

export const useCredentialForm = ({ authorization, onSave }) => {
  const [editMode, setEditMode] = useState(false);
  const [localAuth, setLocalAuth] = useState(null);
  const [attested, setAttested] = useState(false);
  const [noExpirations, setNoExpirations] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);

  const numberValidation = useCredentialNumber();
  const artifactValidation = useCredentialArtifact();
  const npiValidation = useCredentialNpi();
  const referenceValidation = useCredentialReference();

  const auth = editMode && localAuth ? localAuth : authorization;
  const credentials = auth?.credentials || [];

  const handleEdit = useCallback(() => {
    setLocalAuth(structuredClone(authorization));
    setAttested(false);
    setNoExpirations({});
    setShowAllErrors(false);
    setEditMode(true);
  }, [authorization]);

  const handleCancel = useCallback(() => {
    setLocalAuth(null);
    setAttested(false);
    setNoExpirations({});
    setShowAllErrors(false);
    setEditMode(false);
  }, []);

  // --- credential updaters ---

  const updateCredentialById = useCallback((credId, updater) => {
    setLocalAuth((prev) => {
      if (!prev) return prev;
      const newAuth = structuredClone(prev);
      const cred = newAuth.credentials?.find((c) => c?.id === credId);
      if (cred) updater(cred);
      return newAuth;
    });
  }, []);

  const updateCredentialByCredEnum = useCallback((credEnum, updater) => {
    setLocalAuth((prev) => {
      if (!prev) return prev;
      const newAuth = structuredClone(prev);
      const cred = newAuth.credentials?.find((c) => c?.credential === credEnum);
      if (cred) updater(cred);
      return newAuth;
    });
  }, []);

  const updateReferenceByIndex = useCallback((index, updater) => {
    setLocalAuth((prev) => {
      if (!prev) return prev;
      const newAuth = structuredClone(prev);
      const refs = newAuth.credentials?.filter((c) => c?.type === V2Type.Reference) || [];
      if (refs[index]) updater(refs[index]);
      return newAuth;
    });
  }, []);

  const updateCred = useCallback(
    (cred, updater) => {
      if (cred.id) {
        updateCredentialById(cred.id, updater);
      } else {
        updateCredentialByCredEnum(cred.credential, updater);
      }
    },
    [updateCredentialById, updateCredentialByCredEnum],
  );

  // --- no-expiration helpers ---

  const setNoExpiration = useCallback(
    (cred, checked) => {
      const key = getCredKey(cred);
      setNoExpirations((prev) => ({ ...prev, [key]: checked }));
      if (checked) {
        updateCred(cred, (c) => {
          c.expired_at = null;
        });
      }
    },
    [updateCred],
  );

  const getNoExpiration = useCallback(
    (cred) => {
      const key = getCredKey(cred);
      return key in noExpirations ? noExpirations[key] : !cred?.expired_at;
    },
    [noExpirations],
  );

  // --- error accessors ---

  const getFieldError = useCallback(
    (cred, field) => {
      const original = findOriginalCred(authorization, cred);
      const changed = hasCredChanged(cred, original);

      // No change from original → no errors to show
      if (!changed) return null;

      switch (cred?.type) {
        case V2Type.Number: {
          const errors = showAllErrors
            ? numberValidation.validateAll([cred])[getCredKey(cred)] || {}
            : numberValidation.getErrors(cred, original);
          return errors[field] || null;
        }
        case V2Type.Artifact: {
          const noExpiration = getNoExpiration(cred);
          const errors = showAllErrors
            ? artifactValidation.validateAll([cred], noExpirations, getCredKey)[getCredKey(cred)] ||
              {}
            : artifactValidation.getErrors(cred, original, noExpiration);
          return errors[field] || null;
        }
        case V2Type.Npi: {
          const errors = showAllErrors
            ? npiValidation.validateAll([cred])[getCredKey(cred)] || {}
            : npiValidation.getErrors(cred, original);
          return errors[field] || null;
        }
        case V2Type.Reference: {
          const errors = showAllErrors
            ? referenceValidation.validateAll([cred])[getCredKey(cred)] || {}
            : referenceValidation.getErrors(cred, original);
          return errors[field] || null;
        }
        default:
          return null;
      }
    },
    // eslint-disable-next-line max-len
    [
      authorization,
      showAllErrors,
      noExpirations,
      getNoExpiration,
      numberValidation,
      artifactValidation,
      npiValidation,
      referenceValidation,
    ],
  );

  // --- save: only validate & send changed credentials ---

  const handleSave = useCallback(async () => {
    const creds = localAuth?.credentials || [];

    // Filter to only credentials that have actually changed
    const changedCreds = creds.filter((cred) => {
      const original = findOriginalCred(authorization, cred);
      return hasCredChanged(cred, original);
    });

    // Nothing changed → just close edit mode, no API call
    if (changedCreds.length === 0) {
      setEditMode(false);
      return true;
    }

    // Validate only the changed credentials
    const numberCreds = changedCreds.filter((c) => c?.type === V2Type.Number);
    const artifactCreds = changedCreds.filter((c) => c?.type === V2Type.Artifact);
    const npiCreds = changedCreds.filter((c) => c?.type === V2Type.Npi);
    const refCreds = changedCreds.filter((c) => c?.type === V2Type.Reference);

    const numberErrors = numberValidation.validateAll(numberCreds);
    const artifactErrors = artifactValidation.validateAll(artifactCreds, noExpirations, getCredKey);
    const npiErrors = npiValidation.validateAll(npiCreds);
    const refErrors = referenceValidation.validateAll(refCreds);

    const hasErrors =
      Object.keys(numberErrors).length > 0 ||
      Object.keys(artifactErrors).length > 0 ||
      Object.keys(npiErrors).length > 0 ||
      Object.keys(refErrors).length > 0;

    if (hasErrors) {
      setShowAllErrors(true);
      showErrorMessage({ message: 'Please fix the highlighted errors before saving.' });

      // Scroll to the first error field after re-render
      setTimeout(() => {
        const firstError = document.querySelector('[data-field-error="true"]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);

      return false;
    }

    // Build payload with only changed credentials
    const payload = {
      ...localAuth,
      credentials: changedCreds,
    };

    if (onSave) {
      await onSave(payload);
    }
    setEditMode(false);
    return true;
  }, [
    localAuth,
    authorization,
    noExpirations,
    onSave,
    numberValidation,
    artifactValidation,
    npiValidation,
    referenceValidation,
  ]);

  return {
    editMode,
    auth,
    credentials,
    attested,
    setAttested,
    handleEdit,
    handleCancel,
    handleSave,
    updateCred,
    updateReferenceByIndex,
    getFieldError,
    setNoExpiration,
    getNoExpiration,
  };
};
